/**
 * @author Vibhor <vibhor.sharma@314ecorp.com>
 * @description Bulk Provider Upload Modal
 */
import _ from 'lodash';
import { Button, Flex, Modal, Select, Table, Tag, Typography, theme } from 'antd';
import { Download } from '@icon-park/react';
import { useState } from 'react';

import useIngestProviders from 'hooks/admin/mutate/useIngestProviders';
import { CircularSplitter } from 'components/eligibility/batchUpload/dataProcessing/CustomComponents';
import { downloadBase64File } from 'utils/eligibility/eligibilityBatchUpload';

interface IProps {
	open: boolean;
	onClose: () => void;
}

const isValidNpi = (value: string) => /^\d{10}$/.test(value);

const failedColumns = [
	{ title: 'NPI', dataIndex: 'npi', key: 'npi' },
	{ title: 'Failure Reason', dataIndex: 'error', key: 'error' },
];

const AddBulkProviderModal: React.FC<IProps> = ({ open, onClose }) => {
	const { token } = theme.useToken();
	const [npis, setNpis] = useState<string[]>([]);
	const [showSummary, setShowSummary] = useState(false);
	const [summaryData, setSummaryData] = useState<Provider.IBulkPostResponseModel>(null);
	const { mutate, isPending } = useIngestProviders();

	const validNpis = _.filter(npis, isValidNpi);
	const invalidNpis = _.filter(npis, (v) => !isValidNpi(v));

	const handleReset = () => {
		setNpis([]);
		setShowSummary(false);
		setSummaryData(null);
	};

	const handleClose = () => {
		handleReset();
		onClose();
	};

	const tagRender = (props: AnyType) => {
		const { value, closable, onClose: onTagClose } = props;
		const label = _.toString(value);
		const invalid = !isValidNpi(label);
		const displayLabel = _.truncate(label, { length: 17, omission: '..' });
		return (
			<Tag
				className='bulk-npi-tag'
				closable={closable}
				onClose={onTagClose}
				style={invalid ? { borderColor: token.colorError, color: token.colorError } : undefined}
			>
				{displayLabel}
			</Tag>
		);
	};

	const getContent = () => {
		if (showSummary && summaryData) {
			const failedResults = _.filter(summaryData.results, (result) => !result.success);
			return (
				<Flex vertical gap={12} className='max-h-[280px] w-full overflow-hidden'>
					<Flex gap={8} align='center'>
						<Typography.Text type='secondary'>{'Total Records:'}</Typography.Text>
						<Typography.Text strong>{summaryData.total}</Typography.Text>
						<CircularSplitter size='base' />
						<Typography.Text type='secondary'>{'Added:'}</Typography.Text>
						<Typography.Text strong type='success'>
							{summaryData.successful}
						</Typography.Text>
						<CircularSplitter size='base' />
						<Typography.Text type='secondary'>{'Failed:'}</Typography.Text>
						<Typography.Text strong type='danger'>
							{summaryData.failed}
						</Typography.Text>
					</Flex>
					<Flex justify='space-between' className='w-full'>
						<Typography.Text strong>{`Following NPIs could not be added (${summaryData.failed})`}</Typography.Text>
						<Button
							type='text'
							size='small'
							icon={<Download />}
							disabled={!summaryData.excelFile}
							onClick={() => downloadBase64File(summaryData.excelFile, 'failed-providers')}
						/>
					</Flex>
					<Table
						bordered
						size='small'
						columns={failedColumns}
						dataSource={failedResults}
						rowKey='npi'
						pagination={false}
						scroll={{ y: 160 }}
					/>
				</Flex>
			);
		}

		return (
			<Flex vertical gap={8} className='w-full'>
				<Typography.Text type='secondary'>
					{'Use a comma, space, or new line to list NPIs. Only valid 10-digit NPIs are accepted.'}
				</Typography.Text>
				<Select
					mode='tags'
					className='bulk-npi-select w-full'
					suffixIcon={null}
					tokenSeparators={[',', ' ', '\n']}
					open={false}
					filterOption={false}
					disabled={isPending}
					value={npis}
					onChange={setNpis}
					tagRender={tagRender}
					placeholder={'Add NPIs here'}
				/>
				{!_.isEmpty(npis) && (
					<Flex gap={16}>
						<Typography.Text style={{ color: token.colorSuccess }}>{`Valid: ${validNpis.length}`}</Typography.Text>
						<Typography.Text type='danger'>{`Invalid: ${invalidNpis.length}`}</Typography.Text>
					</Flex>
				)}
			</Flex>
		);
	};

	const getFooter = () => {
		if (showSummary) {
			return (
				<Button type='primary' onClick={handleClose}>
					{'Ok'}
				</Button>
			);
		}
		return (
			<Flex gap={8} justify='end'>
				<Button disabled={isPending} onClick={() => setNpis([])}>
					{'Reset'}
				</Button>
				<Button
					type='primary'
					disabled={_.isEmpty(validNpis)}
					loading={isPending}
					onClick={() => {
						const payload: Provider.IBulkPostModel = { providers: _.map(validNpis, (npi) => ({ npi })) };
						mutate(payload, {
							onSuccess: (data) => {
								setSummaryData(data);
								setShowSummary(true);
							},
						});
					}}
				>
					{_.isEmpty(validNpis) ? 'Add' : `Add (${validNpis.length})`}
				</Button>
			</Flex>
		);
	};

	return (
		<Modal
			open={open}
			width={560}
			title={showSummary ? 'Summary' : 'Add Provider NPI(s)'}
			onCancel={handleClose}
			footer={getFooter()}
			closable={!isPending}
			maskClosable={false}
			destroyOnHidden
		>
			{getContent()}
		</Modal>
	);
};

export default AddBulkProviderModal;
